import { useEffect, useState, useCallback } from 'react';

/**
 * §L.0.6 — useTheme hook
 * Modes: 'light' | 'dark' | 'system'.
 * Stored under 'tradelogic.theme' in localStorage.
 * Applies `data-theme` attribute on <html>.
 * FOUC-guard: AppShell's index.html must inline a script that reads the same
 * key BEFORE React mounts.
 */

export type ThemeMode = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'tradelogic.theme';

function systemTheme(): 'light' | 'dark' {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function applyTheme(mode: ThemeMode): void {
  if (typeof document === 'undefined') return;
  const effective = mode === 'system' ? systemTheme() : mode;
  document.documentElement.setAttribute('data-theme', effective);
}

function readStored(): ThemeMode {
  if (typeof window === 'undefined') return 'system';
  const v = window.localStorage.getItem(STORAGE_KEY);
  return v === 'light' || v === 'dark' || v === 'system' ? v : 'system';
}

export function useTheme(): {
  readonly mode: ThemeMode;
  readonly effective: 'light' | 'dark';
  readonly setMode: (m: ThemeMode) => void;
} {
  const [mode, setModeState] = useState<ThemeMode>(() => readStored());
  const [effective, setEffective] = useState<'light' | 'dark'>(() =>
    mode === 'system' ? systemTheme() : mode,
  );

  useEffect(() => {
    applyTheme(mode);
    setEffective(mode === 'system' ? systemTheme() : mode);
    if (mode === 'system' && typeof window !== 'undefined' && window.matchMedia) {
      const mq = window.matchMedia('(prefers-color-scheme: dark)');
      const handler = (): void => {
        applyTheme('system');
        setEffective(systemTheme());
      };
      mq.addEventListener('change', handler);
      return () => mq.removeEventListener('change', handler);
    }
    return undefined;
  }, [mode]);

  const setMode = useCallback((m: ThemeMode) => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, m);
    }
    setModeState(m);
  }, []);

  return { mode, effective, setMode };
}
